import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ConsensusReconciliationSseEventPayload } from '@api-types/sse/event-payloads/consensus-reconciliation.sse-event-payload';
import { SseEventType } from '@api-types/sse/sse.types';
import { SseEventHandler } from '@core/sse/sse-event-handler';
import { Store } from '@ngrx/store';
import { buffer, debounceTime, Subject } from 'rxjs';
import { selectSelectedConsensusTemplateId } from '../router-state/router.selectors';
import {
  type CompletedReconciliation,
  ConsensusGridStateService,
} from './consensus-grid-state.service';
import { ConsensusValuesStateService } from './consensus-values-state.service/consensus-values-state.service';

export const REFRESH_COALESCE_MS = 250;

@Injectable()
export class ConsensusReconciliationSseHandler implements SseEventHandler<
  typeof SseEventType.ConsensusReconciliation
> {
  readonly eventType = SseEventType.ConsensusReconciliation;

  private readonly consensusGridStateService = inject(
    ConsensusGridStateService,
  );
  private readonly consensusValuesStateService = inject(
    ConsensusValuesStateService,
  );
  private readonly store = inject(Store);
  private readonly selectedTemplateId = this.store.selectSignal(
    selectSelectedConsensusTemplateId,
  );

  private readonly completed = new Subject<CompletedReconciliation>();

  constructor() {
    this.completed
      .pipe(
        buffer(this.completed.pipe(debounceTime(REFRESH_COALESCE_MS))),
        takeUntilDestroyed(inject(DestroyRef)),
      )
      .subscribe((batch) => {
        void this.consensusGridStateService.refreshAfterReconciliation(batch);
      });
  }

  handle(payload: ConsensusReconciliationSseEventPayload): void {
    if (payload.consensusTemplateId !== this.selectedTemplateId()) return;

    if (payload.isReconciling) {
      this.consensusGridStateService.addReconcilingScope(
        payload.scope,
        payload.consensusColumnId,
        payload.dateValue,
      );
      return;
    }

    this.consensusGridStateService.markScopeCompletedAwaitingRefresh(
      payload.scope,
      payload.consensusColumnId,
      payload.dateValue,
    );
    this.consensusValuesStateService.evictMonths(
      [payload.dateValue],
      [payload.scope],
    );
    this.completed.next({
      scope: payload.scope,
      consensusColumnId: payload.consensusColumnId,
      dateValue: payload.dateValue,
    });
  }
}
